import { TranscriptSegment } from '@ndvr/customer-api';

import { cn } from '@/utils/cn';

import { formatTranscriptOffset, speakerColor, speakerInitials } from './call-utils';

/**
 * Chat-style rendering of a redacted call transcript. The advisor's lines sit on the
 * right; timestamps seek the recording when one is loaded.
 */
export const CallTranscript = ({
    segments,
    advisorName,
    onSeek,
}: {
    segments: TranscriptSegment[];
    advisorName?: string;
    onSeek?: (seconds: number) => void;
}) => (
    <div className="flex flex-col gap-2.5 pt-4">
        {segments.map((segment, index) => {
            const previous = segments[index - 1];
            const isAdvisor = !!advisorName && segment.speakerName === advisorName;
            const isNewSpeaker = !previous || previous.speakerName !== segment.speakerName;
            const color = speakerColor(segment.speakerName);
            return (
                <div key={index} className={cn('flex items-end gap-2.5', isAdvisor ? 'flex-row-reverse' : 'flex-row')}>
                    <div
                        className={cn(
                            'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 border-white text-[11px] font-semibold',
                            !isNewSpeaker && 'opacity-0',
                        )}
                        style={{ backgroundColor: `${color}29`, color }}
                    >
                        {speakerInitials(segment.speakerName)}
                    </div>
                    <div className={cn('flex max-w-[78%] min-w-0 flex-col', isAdvisor ? 'items-end' : 'items-start')}>
                        {isNewSpeaker && (
                            <div className="mb-1 text-xs font-semibold" style={{ color }}>
                                {segment.speakerName}
                            </div>
                        )}
                        <div
                            className={cn(
                                'rounded-2xl border px-3.5 py-2.5 text-sm leading-relaxed break-words whitespace-pre-wrap',
                                isAdvisor ? 'border-transparent bg-[#946A3E]/10' : 'border-[#CECAC6] bg-white',
                            )}
                        >
                            {segment.text}
                        </div>
                        {segment.offsetMs !== undefined && (
                            <button
                                onClick={onSeek ? () => onSeek(segment.offsetMs! / 1000) : undefined}
                                className={cn(
                                    'text-muted-foreground mt-1 text-[11px] tabular-nums',
                                    onSeek && 'cursor-pointer hover:underline',
                                )}
                                disabled={!onSeek}
                            >
                                {formatTranscriptOffset(segment.offsetMs)}
                            </button>
                        )}
                    </div>
                </div>
            );
        })}
    </div>
);
